import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { getMe } from './utils/queries';

// MSAL Configuration
var redirectUri = "http://localhost:4040";
var clientId = "ebbd6848-d7e3-43e1-9494-b0e60164af5a";
var apiScope = "api://422132b5-d04c-4e43-a6db-6651f01a1109/Read"; //sa-accounts-api-prd

if (process.env.REACT_APP_STAGE === "Dev") {
  redirectUri = "https://app-accounts-finance-dev.azurewebsites.net";
  clientId = "22190f89-fc44-4f5e-91ba-9bee5333d46b"; //sa-accounts-finance-approval-portal-sso-dev
  apiScope = "api://b05c5f1c-6e9d-46ff-9ca5-08702b91c8e7/Read"; //sa-accounts-api-dev
}
if (process.env.REACT_APP_STAGE === "Test") {
  redirectUri = "https://tst.accounts-finance.hafnia.com";
  clientId = "ebbd6848-d7e3-43e1-9494-b0e60164af5a"; //sa-accounts-finance-approval-portal-sso-tst
  apiScope = "api://43c4cdb2-922e-4ff5-83e2-1967c8375573/Read"; //sa-accounts-api-tst
} else if (process.env.REACT_APP_STAGE === "Pre-Production") {
  redirectUri =
    "https://hafniabw-account-approval-finance-pre.azurewebsites.net";
  clientId = "ddfb5d43-7680-4645-a1bb-a336439f36cb";
} else if (process.env.REACT_APP_STAGE === "Production") {
  redirectUri = "https://accounts-finance.hafnia.com";
  clientId = "ddfb5d43-7680-4645-a1bb-a336439f36cb";
}

export const msalConfig = {
  auth: {
    authority:
      "https://login.microsoftonline.com/a364eb28-e95b-4ad0-a4fb-5b4f7767ad84",
    clientId: clientId,
    redirectUri: redirectUri,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false
  }
};

export const loginRequest = {
  scopes: [
    apiScope, // API SCOPE
  ],
};


// Auth Context
export const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [token, setToken] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const userAccount = accounts.length > 0 ? accounts[0] : null;
  const isAuthenticated = !!userAccount;

  const acquireToken = useCallback(async () => {
    if (!userAccount) return null;

    try {
      const request = {
        ...loginRequest,
        account: userAccount
      };
      const response = await instance.acquireTokenSilent(request);
      setToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed, attempting popup:', error);
      try {
        const requestRetry = {
          ...loginRequest,
          account: userAccount
        };
        const response = await instance.acquireTokenPopup(requestRetry);
        setToken(response.accessToken);
        return response.accessToken;
      } catch (popupError) {
        console.error('Token acquisition failed:', popupError);
        return null;
      }
    }
  }, [instance, userAccount]);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
      setToken(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance]);

  // Acquire token when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      acquireToken().catch(err => {
        console.error("Failed to acquire token on init:", err);
      });
    } else {
      setToken(null);
    }
  }, [isAuthenticated, acquireToken]);

  // UI only: the API enforces read-only access.
  useEffect(() => {
    if (!token) return;
    getMe()
      .then(response => setIsReadOnly(!!response.data.isFinanceReadOnly))
      .catch(err => console.error('Failed to load current user:', err));
  }, [token]);

  const contextValue = useMemo(() => ({
    token,
    isAuthenticated,
    isReadOnly,
    login,
    logout,
    acquireToken,
    account: userAccount
  }), [token, isAuthenticated, isReadOnly, userAccount, login, logout, acquireToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
